import React from 'react';

import { NotificationCycle } from '../../../Api/CRM/Responses/ManualNotificationResponse';
import ManualNotification from '../../../Api/Models/ManualNotification';
import { ValidationErrors } from '../../helpers/ResponseErrorHelper';
import useSmartField from '../../hooks/useSmartField';

const cycleNames = Object.entries(NotificationCycle).reduce((a, [k, v]) => ({ ...a, [v]: k }), {});

type Props = {
    notification: ManualNotification;
    setNotification: React.Dispatch<React.SetStateAction<ManualNotification>>;
    endMethod: number;
    setEndMethod: React.Dispatch<React.SetStateAction<number>>;
    errors: ValidationErrors;
};

function NotificationFormFields({ notification, setNotification, endMethod, setEndMethod, errors }: Props) {
    const handleFiledChange = (name: string, value: any) => setNotification((prev) => prev.getNew({ [name]: value }));
    const Field = useSmartField({ errors, onChange: handleFiledChange });

    return (
        <>
            <Field name="title" value={notification.title} required />
            <Field type="texteditor" name="content" value={notification.content} required />
            <Field type="number" name="pinned_hours" value={notification.pinned_hours} min={0} required />
            <Field type="checkbox" name="is_active" value={notification.is_active} />
            <Field
                type="date"
                name="start_date"
                value={notification.start_date}
                min={notification.start_date}
                required
            />
            <Field
                type="select"
                name="cycle"
                label="Notification cycle"
                value={notification.cycle}
                options={cycleNames}
                required
            />
            {notification.cycle === NotificationCycle.Daily ? null : (
                <Field
                    name="cycle_days"
                    label={`Cycle days ${getLabelForCycleDays(notification.cycle)}`}
                    value={notification.cycle_days?.join(',')}
                    onChange={(name, value) =>
                        setNotification((prev) => prev.getNew({ [name]: cleanCycleDays(value) }))
                    }
                    required
                />
            )}
            <Field
                type="time"
                name="deliver_time"
                label="Deliver time (no earlier than)"
                value={notification.deliver_time}
                required
            />
            <Field
                type="select"
                name="endMethod"
                value={endMethod}
                label="Cycle termination"
                options={{ 0: 'Sending counter', 1: 'On a chosen day' }}
                onChange={(name, value) => setEndMethod(parseInt(value))}
                required
            />
            {endMethod === 0 ? (
                <Field type="number" name="left_to_deliver" value={notification.left_to_deliver} min={1} required />
            ) : (
                <Field
                    type="date"
                    name="end_date"
                    value={notification.end_date}
                    min={notification.start_date}
                    required
                />
            )}
        </>
    );
}

function getLabelForCycleDays(cycle: NotificationCycle): string {
    return {
        [NotificationCycle.Weekly]: '(1 through 7, Mon/Sun, comma separated)',
        [NotificationCycle.Monthly]: '(1 through 31, comma separated)',
        [NotificationCycle.Yearly]: '(1 through 366, comma separated)',
    }[cycle];
}

function cleanCycleDays(input: string) {
    const numbers = (input ?? '').split(/\D+/);

    return numbers.map((v, i) => parseInt(v) || (i === numbers.length - 1 ? '' : null)).filter((v) => v !== null);
}

export default NotificationFormFields;
